import type { ReactNode } from 'react';
import InlineStatus from './InlineStatus';
import type { AutomationOutcomeHandoffContext } from '../../lib/automationOutcomeHandoff';
import {
  getAutomationOutcomeDetailLabel,
  getAutomationOutcomeDetailText,
  getAutomationOutcomeRecordedAnswer,
  getAutomationOutcomeStateLabel,
} from '../../lib/automationOutcomeHandoff';

interface AutomationOutcomeRailProps {
  handoff: AutomationOutcomeHandoffContext;
  nextManualFollowUp: string;
  title?: string;
  message?: ReactNode;
  pinecurlAction?: ReactNode;
  helper?: ReactNode;
  action?: ReactNode;
  className?: string;
  testId?: string;
}

export default function AutomationOutcomeRail({
  handoff,
  nextManualFollowUp,
  title = handoff.outcomeState === 'matched'
    ? 'Matched automation proof returned here'
    : handoff.outcomeState === 'unmatched'
      ? 'Unmatched automation context returned here'
      : 'No-execution automation context returned here',
  message = handoff.outcomeState === 'matched'
    ? 'Automation recorded this advisory match on /automation. This owner route still holds the next manual follow-up.'
    : handoff.outcomeState === 'unmatched'
      ? 'Automation is still watching this saved rule on /automation. This owner route keeps the next manual follow-up ready for a future match.'
      : 'Automation has not recorded an execution for this saved rule on /automation yet. This owner route keeps the next manual follow-up truthful without inflating urgency.',
  pinecurlAction,
  helper,
  action,
  className = '',
  testId,
}: AutomationOutcomeRailProps) {
  return (
    <div data-testid={testId}>
      <InlineStatus
        tone="info"
        title={title}
        message={(
          <div className="space-y-3">
            <div className="monitoring-loop-proof-grid">
              <div className="monitoring-loop-proof-chip">
                <p className="monitoring-loop-proof-chip__label">
                  Source route
                </p>
                <p className="monitoring-loop-proof-chip__value">Automation</p>
              </div>
              <div className="monitoring-loop-proof-chip">
                <p className="monitoring-loop-proof-chip__label">
                  Watched rule
                </p>
                <p className="monitoring-loop-proof-chip__value">{handoff.ruleLabel}</p>
              </div>
              <div className="monitoring-loop-proof-chip">
                <p className="monitoring-loop-proof-chip__label">
                  Arrival state
                </p>
                <p className="monitoring-loop-proof-chip__value">{getAutomationOutcomeStateLabel(handoff.outcomeState)}</p>
              </div>
              <div className="monitoring-loop-proof-chip">
                <p className="monitoring-loop-proof-chip__label">
                  Recorded time
                </p>
                <p className="monitoring-loop-proof-chip__value font-mono tabular-nums">
                  {getAutomationOutcomeRecordedAnswer(handoff)}
                </p>
              </div>
              <div className="monitoring-loop-proof-chip">
                <p className="monitoring-loop-proof-chip__label">
                  {getAutomationOutcomeDetailLabel(handoff)}
                </p>
                <p className="monitoring-loop-proof-chip__value">{getAutomationOutcomeDetailText(handoff)}</p>
              </div>
              {pinecurlAction ? (
                <div className="monitoring-loop-proof-chip">
                  <p className="monitoring-loop-proof-chip__label">
                    What Pinecurl already did
                  </p>
                  <p className="monitoring-loop-proof-chip__value">{pinecurlAction}</p>
                </div>
              ) : null}
              <div className="monitoring-loop-proof-chip">
                <p className="monitoring-loop-proof-chip__label">
                  Next manual follow-up
                </p>
                <p className="monitoring-loop-proof-chip__value">{nextManualFollowUp}</p>
              </div>
            </div>
            <p className="text-sm text-gray-300">{message}</p>
            {helper ? <div className="text-xs text-gray-400">{helper}</div> : null}
          </div>
        )}
        action={action}
        className={`${className || 'monitoring-loop-support-status rounded-xl'}`.trim()}
      />
    </div>
  );
}
